import { useEffect, useRef } from "react";
import { confetti } from "@tsparticles/confetti";

interface ConfettiAnimationProps {
  containerRef: React.RefObject<HTMLElement | null>;
  visibleFraction?: number; // 0.25 for quarter, 0.5 for half, etc.
  trigger?: boolean; // If true, fire confetti immediately
}

export const ConfettiAnimation = ({
  containerRef,
  visibleFraction = 0.5,
  trigger = false,
}: ConfettiAnimationProps) => {
  const scrollTriggered = useRef(false);
  const prevTrigger = useRef(false);
  const usingTriggerMode = useRef(false);

  const fireConfetti = (containerElement: HTMLElement) => {
    const rect = containerElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    confetti({
      particleCount: 300,
      colors: [
        "#FF5257",
        "#61BA47",
        "#007AFF",
        "#F74F9E",
        "#A550A7",
        "#FFC700",
      ],
      shapes: ["triangle", "square"],
      zIndex: 9999,
      disableForReducedMotion: true,
      gravity: 1,
      drift: 0,
      origin: { x: centerX / window.innerWidth, y: 0.5 },
      spread: 50,
      startVelocity: 35,
    });
  };

  useEffect(() => {
    if (trigger && !prevTrigger.current && containerRef.current) {
      usingTriggerMode.current = true;
      fireConfetti(containerRef.current);
    }
    prevTrigger.current = trigger;
  }, [trigger, containerRef]);

  useEffect(() => {
    if (usingTriggerMode.current) return;

    function checkAndTrigger() {
      if (!containerRef.current || scrollTriggered.current) return;
      const rect = containerRef.current.getBoundingClientRect();

      const containerHeight = rect.height;
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, window.innerHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight >= containerHeight * visibleFraction) {
        scrollTriggered.current = true;
        fireConfetti(containerRef.current);
      }
    }

    window.addEventListener("scroll", checkAndTrigger);
    window.addEventListener("resize", checkAndTrigger);
    checkAndTrigger();

    return () => {
      window.removeEventListener("scroll", checkAndTrigger);
      window.removeEventListener("resize", checkAndTrigger);
    };
  }, [containerRef, visibleFraction]);

  return null;
};
